import * as angular from "angular";
import { ResourceType } from "@enums/ResourceType";
import { IResponse } from "@interfaces/IResponse";
import { IExternalReference, IExternalReferenceType } from "./externalReferences.interfaces";
import { TranslationService } from "@services/translation.service";
import { CacheService } from "@services/cache.Service";

export class ExternalReferenceService {

    static $inject = ["$q", "$http", "translationService", "cacheService"];

    updatedExternalRef: IExternalReference = null;
    addedExternalRef: IExternalReference = null;
    deleteExternalRef: IExternalReference = null;

    private externalReferenceTypesCacheName = "externalReferenceTypesCacheName"

    private driverExternalReferenceCacheName = "driverExternalReverenceCache";
    private vehicleExternalReferenceCacheName = "vehicleExternalReverenceCache";
    private geofenceExternalReferenceCacheName = "geofenceExternalReverenceCache";

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly translationService: TranslationService,
        private readonly cacheService: CacheService
    ) {

        this.cacheService.createCache(this.externalReferenceTypesCacheName, { maxAge: 30 * 60 * 1000 });

        this.cacheService.createCache(this.driverExternalReferenceCacheName, { maxAge: 30 * 60 * 1000 });
        this.cacheService.createCache(this.vehicleExternalReferenceCacheName, { maxAge: 30 * 60 * 1000 });
        this.cacheService.createCache(this.geofenceExternalReferenceCacheName, { maxAge: 30 * 60 * 1000 });
    }

    getExternalTypes(invalidateCache = false): angular.IPromise<IExternalReferenceType[]> {
        const cacheName = this.externalReferenceTypesCacheName;

        return this.cacheService.getOrRetrieveData<IExternalReferenceType[]>(cacheName, () => this.getExternalTypesFromDB(), invalidateCache);
    }

    getExternalTypesFromDB(): angular.IPromise<IExternalReferenceType[]> {
        return this.$http({
            url: "/api/externalreference/types/",
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReferenceType[]>) => response.data);
    }

    getExternalTypesByType(type: string, invalidateCache = false): angular.IPromise<IExternalReferenceType[]> {

        if (!type || type.trim().length === 0) {
            return this.$q.when([]);
        }

        return this.$http({
            url: `/api/externalreference/types/${type}/`,
            method: "GET",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        })
            .then((response: angular.IHttpPromiseCallbackArg<IExternalReferenceType[]>) => {
                return response.data;
            });
    }

    private setUpdatedExtReferenceType(externalTypes: IExternalReferenceType[]) {
        const extTypes = externalTypes.filter(x => x.Id === +this.updatedExternalRef.ExternalReferenceTypeId);
        if (extTypes && extTypes.length > 0) {
            this.updatedExternalRef.externalReferenceType = angular.copy(extTypes[0]);
        }
    }

    private getExtRefIndex(id: number, externalReferenceList: IExternalReference[]): number {
        for (let i = 0; i < externalReferenceList.length; i++) {
            if (externalReferenceList[i].Id === id) {
                return i;
            }
        }
        return -1;
    }

    updateCachedValues(externalReferenceList: IExternalReference[], externalTypes: IExternalReferenceType[]): void {
        if (this.updatedExternalRef) {
            const index = this.getExtRefIndex(this.updatedExternalRef.Id, externalReferenceList);
            if (index >= 0) {
                if (!this.updatedExternalRef.externalReferenceType) {
                    this.setUpdatedExtReferenceType(externalTypes);
                }
                externalReferenceList.splice(index, 1, angular.copy(this.updatedExternalRef));
            }
            this.updatedExternalRef = null;
        }

        if (this.addedExternalRef) {
            const index = this.getExtRefIndex(this.addedExternalRef.Id, externalReferenceList);
            if (index < 0) {
                externalReferenceList.push(angular.copy(this.addedExternalRef));
                this.addedExternalRef = null;
            }
        }

        if (this.deleteExternalRef) {
            const index = this.getExtRefIndex(this.deleteExternalRef.Id, externalReferenceList);
            if (index >= 0) {
                externalReferenceList.splice(index, 1);
            }
            this.deleteExternalRef = null;
        }
    }

    getReferencesById(cacheName: string, id: number, matchingPredicate: (item) => boolean, retrievalCallback: () => angular.IPromise<IExternalReference[]> ): angular.IPromise<IExternalReference[]> {
        const externalReferences = this.cacheService.get<IExternalReference[]>(cacheName)?.filter(matchingPredicate);

        return externalReferences?.length ? this.$q.when(externalReferences) : retrievalCallback();
    }

    getAllDriverExternalReferences(invalidateCache = false): angular.IPromise<IExternalReference[]> {
        const cacheName = this.driverExternalReferenceCacheName;

        return this.cacheService.getOrRetrieveData(cacheName, () => this.getAllDriverExternalReferencesFromDB(), invalidateCache);
    }

    getAllDriverExternalReferencesFromDB(): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: "/api/externalreference/driver/all/",
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => response.data);
    }

    getDriverExternalReferences(driverId: number): angular.IPromise<IExternalReference[]> {

        const cacheName = this.driverExternalReferenceCacheName;
        const matchingPredicate = (externalReference) => externalReference.DriverId == driverId;
        const retrievalCallback = () => this.getDriverExternalReferencesFromDB(driverId);

        return this.getReferencesById(cacheName, driverId, matchingPredicate, retrievalCallback);
    }

    getDriverExternalReferencesFromDB(driverId: number): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: `/api/externalreference/driver/${driverId}/`,
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => {

            response.data?.forEach(externalReference => {
                this.cacheService.addOrUpdateItem<IExternalReference>(this.driverExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);
            })

            return response.data;
        });
    }

    getAllGeofenceExternalReferences(invalidateCache = false): angular.IPromise<IExternalReference[]> {
        const cacheName = this.geofenceExternalReferenceCacheName;

        return this.cacheService.getOrRetrieveData(cacheName, () => this.getAllGeofenceExternalReferencesFromDB(), invalidateCache);
    }

    getAllGeofenceExternalReferencesFromDB(): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: "/api/externalreference/geofence/all/",
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => response.data);
    }

    getGeofenceExternalReferences(geofenceId: number): angular.IPromise<IExternalReference[]> {
        const cacheName = this.geofenceExternalReferenceCacheName;
        const matchingPredicate = (externalReference) => externalReference.GeofenceId == geofenceId;
        const retrievalCallback = () => this.getGeofenceExternalReferencesFromDB(geofenceId);

        return this.getReferencesById(cacheName, geofenceId, matchingPredicate, retrievalCallback);
    }


    getGeofenceExternalReferencesFromDB(geofenceId: number): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: `/api/externalreference/geofence/${geofenceId}/`,
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => {

            response.data?.forEach(externalReference => {
                this.cacheService.addOrUpdateItem<IExternalReference>(this.geofenceExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);
            })

            return response.data;
        });
    }

    getAllVehicleExternalReferences(invalidateCache = false): angular.IPromise<IExternalReference[]> {
        const cacheName = this.vehicleExternalReferenceCacheName;

        return this.cacheService.getOrRetrieveData(cacheName, () => this.getAllVehicleExternalReferencesFromDB(), invalidateCache);
    }

    getAllVehicleExternalReferencesFromDB(): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: "/api/externalreference/vehicle/all/",
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => response.data);
    }

    getVehicleExternalReferences(vehicleId: number, invalidateCache = false): angular.IPromise<IExternalReference[]> {
        const cacheName = this.vehicleExternalReferenceCacheName;
        const matchingPredicate = (externalReference) => externalReference.VehicleId == vehicleId;
        const retrievalCallback = () => this.getVehicleExternalReferencesFromDB(vehicleId);

        return this.getReferencesById(cacheName, vehicleId, matchingPredicate, retrievalCallback);
    }

    getVehicleExternalReferencesFromDB(vehicleId: number): angular.IPromise<IExternalReference[]> {
        return this.$http({
            url: `/api/externalreference/vehicle/${vehicleId}/`,
            method: "GET"
        })
        .then((response: angular.IHttpPromiseCallbackArg<IExternalReference[]>) => {

            response.data?.forEach(externalReference => {
                this.cacheService.addOrUpdateItem<IExternalReference>(this.vehicleExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);
            })

            return response.data
        });
    }

    deleteDriverExternalReference(referenceId: number, invalidateCache = false) {
        return this.$http({
            url: `/api/externalreference/driver/${referenceId}/`,
            method: "DELETE",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then(result => {

            this.cacheService.deleteItem<Partial<IExternalReference>>(this.driverExternalReferenceCacheName, { Id: referenceId }, (i1, i2) => i1.Id == i2.Id);

            return this.getAllDriverExternalReferences().then(() => { return result });
        });
    }

    deleteGeofenceExternalReference(referenceId: number, invalidateCache = false) {
        return this.$http({
            url: `/api/externalreference/geofence/${referenceId}/`,
            method: "DELETE",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then(result => {

            this.cacheService.deleteItem<Partial<IExternalReference>>(this.geofenceExternalReferenceCacheName, { Id: referenceId }, (i1, i2) => i1.Id == i2.Id);

            return this.getAllGeofenceExternalReferences().then(() => { return result });
        });
    }

    deleteVehicleExternalReference(referenceId: number, invalidateCache = false) {
        return this.$http({
            url: `/api/externalreference/vehicle/${referenceId}/`,
            method: "DELETE",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then(result => {

            this.cacheService.deleteItem<Partial<IExternalReference>>(this.vehicleExternalReferenceCacheName, { Id: referenceId }, (i1, i2) => i1.Id == i2.Id);

            return this.getAllVehicleExternalReferences().then(() => { return result });
        });
    }

    updateDriverExternalReference(externalReference: IExternalReference, invalidateCache = false): angular.IPromise<any> {
        return this.$http({
            url: "/api/externalreference/drivers/",
            method: "POST",
            data: externalReference,
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then((result: angular.IHttpPromiseCallbackArg<any>) => {
            (externalReference.Id <= 0) && (externalReference.Id = result.data);
            this.cacheService.addOrUpdateItem<IExternalReference>(this.driverExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);

            return this.getAllDriverExternalReferences().then(() => result);
        });
    }

    updateGeofenceExternalReference(externalReference: IExternalReference, invalidateCache = false): angular.IPromise<any> {
        return this.$http({
            url: "/api/externalreference/geofences/",
            method: "POST",
            data: externalReference,
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then((result: angular.IHttpPromiseCallbackArg<any>) => {
            (externalReference.Id <= 0) && (externalReference.Id = result.data);
            this.cacheService.addOrUpdateItem<IExternalReference>(this.geofenceExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);

            return this.getAllGeofenceExternalReferences().then(() => result);
        });
    }

    updateVehicleExternalReference(externalReference: IExternalReference, invalidateCache = false): angular.IPromise<any> {
        return this.$http({
            url: "/api/externalreference/vehicles/",
            method: "POST",
            data: externalReference,
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        }).then((result: angular.IHttpPromiseCallbackArg<any>) => {
            (externalReference.Id <= 0) && (externalReference.Id = result.data);
            this.cacheService.addOrUpdateItem<IExternalReference>(this.vehicleExternalReferenceCacheName, externalReference, (i1, i2) => i1.Id == i2.Id);

            return this.getAllVehicleExternalReferences().then(() => result);
        });
    }

    getResourceType(externalReference: IExternalReference): ResourceType {
        if (externalReference.DriverId) return ResourceType.Driver;
        if (externalReference.GeofenceId) return ResourceType.Geofence;
        if (externalReference.VehicleId) return ResourceType.Vehicle;

        return null;
    }

    validateExternalReference(externalReference: IExternalReference): angular.IPromise<IResponse> {

        return this.getByExternalReference(externalReference.ExternalReference, this.getResourceType(externalReference), true)
            .then(externalReferences => {
                const response: IResponse = {
                    Success: true,
                    Message: ""
                };

                if (!externalReferences) {
                    response.Success = false;
                    response.Message = this.translationService.t("externalReference.checkDuplicateReferenceError");
                    return response;
                }

                const results = externalReferences
                    .filter(ref => externalReference.Id === 0 || externalReference.Id !== ref.Id)
                    .firstOrDefault<IExternalReference>();

                if (results) {
                    response.Success = false;
                    response.Message = this.translationService.t("externalReference.duplicateReference");
                }
                return response;
            });

    }


    getByExternalReference(externalReference: string, resourceType: ResourceType, invalidateCache = false): angular.IPromise<IExternalReference[]> {
        let url: string = "";

        if (!externalReference || externalReference.trim().length === 0) {
            return this.$q.when([]);
        }

        switch (resourceType) {
            case ResourceType.Driver:
                url = `/api/externalreference/driver/ref/${externalReference}/`;
                break;

            case ResourceType.Geofence:
                url = `/api/externalreference/geofence/ref/${externalReference}/`;
                break;

            case ResourceType.Vehicle:
                url = `/api/externalreference/vehicle/ref/${externalReference}/`;
                break;
        }

        return (this.$http({
                url: url,
                method: "GET",
                params: { minsToLive: 30, invalidateCache: invalidateCache, failureMessage: 'Failed to get external references' }
            }).then(results => {
                return results.data;
            })
            .catch(e => {
                return null;
            })
        ) as angular.IPromise<IExternalReference[]>;
    }

    getAllReferenceData(resourceType: ResourceType, invalidateCache = false): angular.IPromise<IExternalReference[]> {
        switch (resourceType) {
            case ResourceType.Driver:

                return this.getAllDriverExternalReferences(invalidateCache);

            case ResourceType.Geofence:

                return this.getAllGeofenceExternalReferences(invalidateCache);

            case ResourceType.Vehicle:

                return this.getAllVehicleExternalReferences(invalidateCache);

            default:
                return null;
        }
    }

    getExtReferenceData(externalTypes: IExternalReferenceType[], id: number, resourceType: ResourceType, invalidateCache = false): angular.IPromise<IExternalReference[]> {

        let promise: angular.IPromise<IExternalReference[]>;

        switch (resourceType) {
            case ResourceType.Driver:
                promise = this.getDriverExternalReferences(id);
                break;

            case ResourceType.Geofence:
                promise = this.getGeofenceExternalReferences(id);
                break;

            case ResourceType.Vehicle:
                promise = this.getVehicleExternalReferences(id);
                break;

            default:
                return null;
        }

        return promise.then(externalReferences => {

            externalReferences.forEach(er => {
                er.externalReferenceType = externalTypes.filter(type => type.Id === er.ExternalReferenceTypeId).firstOrDefault<IExternalReferenceType>();
            });

            return externalReferences;
        });
    }

    deleteReference(externalReference: IExternalReference): angular.IPromise<angular.IHttpResponse<{}>> {
        this.initExternalRefChangeFields();

        switch (this.getResourceType(externalReference)) {
            case ResourceType.Driver:
                this.deleteExternalRef = externalReference;
                return this.deleteDriverExternalReference(externalReference.Id, true);

            case ResourceType.Geofence:
                return this.deleteGeofenceExternalReference(externalReference.Id, true);

            case ResourceType.Vehicle:
                return this.deleteVehicleExternalReference(externalReference.Id, true);

            default:
                return null;
        }
    }

    updateReference(externalReference: IExternalReference): angular.IPromise<IResponse> {

        this.initExternalRefChangeFields();

        return this.validateExternalReference(externalReference).then(result => {

            if (result.Success) {

                switch (this.getResourceType(externalReference)) {
                    case ResourceType.Driver:
                        result.Data = this.updateDriverExternalReference(externalReference).then((response) => {
                            result.Data = response;
                            this.setExternalRefChangeFields(externalReference, response.data);
                        });
                        break;

                    case ResourceType.Geofence:
                        result.Data = this.updateGeofenceExternalReference(externalReference);
                        break;

                    case ResourceType.Vehicle:
                        result.Data = this.updateVehicleExternalReference(externalReference).then((response) => {
                            result.Data = response;
                            this.setExternalRefChangeFields(externalReference, response.data);
                        });
                        break;
                }
            }

            return result;
        });
    }

    updateExternalReferenceType(externalReferenceType: IExternalReferenceType): angular.IPromise<any> {

        if (!externalReferenceType) {
            return this.$q.when(null);
        }

        return this.getExternalTypesByType(externalReferenceType.Type, true).then(types => {
            var existingType = types.firstOrDefault<IExternalReferenceType>();
            if (existingType) {
                return existingType.Id;
            }

            return this.addExternalReferenceType(externalReferenceType);
        });
    }

    addExternalReferenceType(externalReferenceType: IExternalReferenceType): angular.IPromise<number> {

        return this
            .$http({
                url: "/api/externalreference/types",
                method: "POST",
                data: externalReferenceType
            })
            .then((response: angular.IHttpPromiseCallbackArg<number>) => {
                this.cacheService.addOrUpdateItem(this.externalReferenceTypesCacheName, { ...externalReferenceType, Id: response.data }, (i1, i2) => i1.Id == i2.Id);
                return response.data;
            });
    }

    setExternalRefChangeFields(externalReference, refId: number) {
        const addMode = externalReference.Id ? false : true;
        switch (addMode) {
            case true:
                this.addedExternalRef = externalReference;
                this.addedExternalRef.Id = refId;
                break;
            default:
                this.updatedExternalRef = externalReference;
                break;
        }
    }

    initExternalRefChangeFields() {
        this.updatedExternalRef = null;
        this.addedExternalRef = null;
        this.deleteExternalRef = null;
    }
}

